import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Tooltip,
  Box,
  Chip,
  Typography,
  IconButton,
  Divider,
  Checkbox,
} from "@mui/material";
import Pagination from "@mui/lab/Pagination";
import * as actions from "../Releases/actions";
import { User, Mail, Music, Disc, Calendar, Shield, ExternalLink, Scale, Copy, ClipboardCheck } from 'lucide-react';
import _ from "lodash";
import { toastError, toastSuccess, copyToClipboard } from "../../utils";
import CustomShowModal from "../Common/customShowModal";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import ActionButton from '../../components/ActionButton';

const glowKeyframes = `
@keyframes glow-success {
  0%, 100% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.4); }
  50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.7); }
}
@keyframes glow-danger {
  0%, 100% { box-shadow: 0 0 8px rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.7); }
}
`;

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function UGC(props) {
  const [UGCNew, setUGCNew] = useState([]);
  const [UGCOld, setUGCOld] = useState([]);
  const [actionsModal, setActionsModal] = useState(false);
  const [linksModal, setLinksModal] = useState(false);
  const [linksList, setLinksList] = useState([]);
  const [count, setCount] = useState(0);
  const [actionId, setActionId] = useState();
  const [selectedClaims, setSelectedClaims] = useState([]);

  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetUGCOld(filters), 500),
    []
  );

  useEffect(() => {
    document.title = 'UG | UGC Claims';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    onGetUGCOld(filters);
    setTimeout(() => {
      props.setLoader(false);
    }, 5000);
  }, []);

  const onGetUGCOld = (filters) => {
    props.setLoader(true);
    actions
      .getUGCClaimOldAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setUGCOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetUGCOld(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onGetNewUGC = () => {
    props.setLoader(true);
    actions
      .getUGCClaimRequestAdmin()
      .then((res) => {
        setUGCNew(res.body);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load UGC claims');
      });
  };

  useEffect(() => {
    onGetNewUGC();
  }, []);

  const handleLinksOnClick = (list) => {
    setLinksList(list);
    setLinksModal(true);
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const handleActionOnClick = (item) => {
    setActionsModal(true);
    setActionId(item);
  };

  const onConfirmAcceptModal = (status) => {
    props.setLoader(true);
    actions
      .updateUGCstatusAdmin({ status: status, claim_id: actionId })
      .then((res) => {
        setActionsModal(false);
        onGetNewUGC();
        onGetUGCOld(filters);
        toastSuccess(res.message || 'UGC status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update UGC status');
      });
  };

  // Bulk selection helpers
  const toggleClaimSelection = (claim_id) => {
    setSelectedClaims(prev =>
      prev.includes(claim_id) ? prev.filter(id => id !== claim_id) : [...prev, claim_id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClaims.length === UGCNew.length) {
      setSelectedClaims([]);
    } else {
      setSelectedClaims(UGCNew.map(item => item.claim_id));
    }
  };

  const onBulkAction = (status) => {
    props.setLoader(true);
    actions
      .bulkUpdateUGCstatusAdmin({ claim_ids: selectedClaims, status })
      .then((res) => {
        setSelectedClaims([]);
        onGetNewUGC();
        onGetUGCOld(filters);
        toastSuccess(res.message || 'Bulk UGC status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update UGC status');
      });
  };

  const renderClaimCard = (item, isNew) => (
    <Paper
      key={item.claim_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      {isNew && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pl: 0.5,
            flexShrink: 0,
          }}
        >
          <Checkbox
            checked={selectedClaims.includes(item.claim_id)}
            onChange={() => toggleClaimSelection(item.claim_id)}
            size="small"
            sx={{
              color: '#64748B',
              '&.Mui-checked': { color: '#6C63FF' },
            }}
          />
        </Box>
      )}
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <Scale size={20} color="#64748B" />
          <span>UGC</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.release_title || 'Untitled'}
            </Typography>
          </div>
          <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <ActionButton variant="info" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }} onClick={() => handleLinksOnClick(item.url)}>
              View Links
            </ActionButton>
            {item.url && item.url.length > 0 && (
              <CopyLinksButton urls={item.url} />
            )}
            <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'center' }}>
              {isNew ? (
                <Box display="flex" gap={0.75}>
                  <ActionButton
                    variant="success"
                    sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }}
                    onClick={() => handleActionOnClick(item.claim_id)}
                    disabled={selectedClaims.length > 0}
                  >
                    Accept
                  </ActionButton>
                  <ActionButton
                    variant="danger"
                    sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }}
                    onClick={() => handleActionOnClick(item.claim_id)}
                    disabled={selectedClaims.length > 0}
                  >
                    Reject
                  </ActionButton>
                </Box>
              ) : (
                <StatusBadge status={item.status} />
              )}
            </Box>
          </div>
        </div>

        <div className="approved-release-card__meta">
          <CopyableId value={`FM-UGC-${item.claim_id}`} />
          <CopyableId value={`RL-${item.release_id}`} />
          <CopyMeta icon={<Music size={13} />} label="Audio" value={item.audio_title} />
          <CopyMeta icon={<User size={13} />} label="User" value={item.name} />
        </div>
        <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          <CopyMeta icon={<Disc size={13} />} label="Platform" value={item.platform} />
          <CopyMeta icon={<Shield size={13} />} label="Policy" value={item.policy} />
          {item.date && (
            <CopyMeta icon={<Calendar size={13} />} label="Date" value={item.date} />
          )}
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      <style>{glowKeyframes}</style>

      {/* ========== Review Required ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Review Required
          </Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            {selectedClaims.length > 0 && (
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  backgroundColor: 'rgba(108, 99, 255, 0.08)',
                  border: '1px solid rgba(108, 99, 255, 0.2)',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Checkbox
                    checked={selectedClaims.length === UGCNew.length}
                    indeterminate={selectedClaims.length > 0 && selectedClaims.length < UGCNew.length}
                    onChange={toggleSelectAll}
                    size="small"
                    sx={{
                      color: '#64748B',
                      '&.Mui-checked': { color: '#6C63FF' },
                      '&.MuiCheckbox-indeterminate': { color: '#6C63FF' },
                    }}
                  />
                  <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {selectedClaims.length} selected
                  </Typography>
                </Box>
                <ActionButton
                  variant="success"
                  onClick={() => onBulkAction("approved")}
                  sx={{
                    py: 0.6,
                    px: 2.5,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    animation: 'glow-success 2s ease-in-out infinite',
                  }}
                >
                  Accept Selected
                </ActionButton>
                <ActionButton
                  variant="danger"
                  onClick={() => onBulkAction("rejected")}
                  sx={{
                    py: 0.6,
                    px: 2.5,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    animation: 'glow-danger 2s ease-in-out infinite',
                  }}
                >
                  Reject Selected
                </ActionButton>
              </Box>
            )}
            <Chip
              icon={<Scale size={14} />}
              label={`${UGCNew.length} Pending`}
              sx={{
                backgroundColor: 'rgba(245, 158, 11, 0.12)',
                color: '#F59E0B',
                border: '1px solid rgba(245, 158, 11, 0.25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#F59E0B' },
              }}
            />
          </Box>
        </Box>
        <Box className="approved-release-list" mb={4}>
          {UGCNew.length ? (
            UGCNew.map((item) => renderClaimCard(item, true))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Claims To Review" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
      </Grid>

      {/* ========== UGC History ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            UGC History
          </Typography>
          <Chip
            icon={<Scale size={14} />}
            label={`${count} Total`}
            sx={{
              backgroundColor: 'rgba(108, 99, 255, 0.12)',
              color: '#6C63FF',
              border: '1px solid rgba(108, 99, 255, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#6C63FF' },
            }}
          />
        </Box>
        <Box mb={3} sx={{ maxWidth: 420 }}>
          <SearchInput
            onChange={onChangeSearch}
            value={filters.search}
            placeholder="Search by name, email, or release..."
          />
        </Box>
        <Box className="approved-release-list">
          {UGCOld.length ? (
            UGCOld.map((item) => renderClaimCard(item, false))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No UGC History Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#94A3B8',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(108, 99, 255, 0.2)',
                    color: '#6C63FF',
                  },
                },
              }}
            />
          </Box>
        )}
      </Grid>

      {/* ========== Modals ========== */}
      <CustomShowModal
        handleClose={() => setActionsModal(false)}
        title="What's your action?"
        open={actionsModal}
        maxWidth="sm"
      >
        <Box display="flex" justifyContent="center" gap={2} py={2}>
          <ActionButton variant="success" onClick={() => onConfirmAcceptModal("approved")}>
            Accept
          </ActionButton>
          <ActionButton variant="danger" onClick={() => onConfirmAcceptModal("rejected")}>
            Reject
          </ActionButton>
        </Box>
      </CustomShowModal>

      <CustomShowModal
        handleClose={() => setLinksModal(false)}
        title="Claim URLs"
        open={linksModal}
        maxWidth="sm"
      >
        {linksList.length > 0 ? (
          <Box py={1}>
            {linksList.map((value, idx) => (
              <CopyableURL key={idx} index={idx} value={value} />
            ))}
          </Box>
        ) : (
          <Box py={3} textAlign="center">
            <Typography sx={{ color: '#64748B', fontSize: '0.9rem' }}>
              No links added by user
            </Typography>
          </Box>
        )}
      </CustomShowModal>
    </Grid>
  );
}

function CopyableId({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
      <span
        className="approved-release-card__id"
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {value}
        {copied ? <ClipboardCheck size={11} color="#10B981" /> : <Copy size={11} />}
      </span>
    </Tooltip>
  );
}

function CopyLinksButton({ urls }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy all links"} arrow placement="top">
      <IconButton
        size="small"
        onClick={() => {
          copyToClipboard(urls.join(',\n'));
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        sx={{
          color: copied ? '#10B981' : '#64748B',
          '&:hover': { color: 'var(--primary)' },
        }}
      >
        {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
      </IconButton>
    </Tooltip>
  );
}

function CopyableURL({ index, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box className="copyable-field" sx={{ py: 1 }}>
      <span className="copyable-field__label" style={{ minWidth: 40, width: 40 }}>
        {index + 1}.
      </span>
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        style={{
          color: 'var(--primary)',
          fontSize: '0.85rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {value}
      </a>
      <Tooltip title={copied ? "Copied!" : "Copy URL"} arrow placement="top">
        <IconButton
          size="small"
          onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          sx={{ color: copied ? '#10B981' : 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, ml: 0.5 }}
        >
          {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in new tab" arrow placement="top">
        <IconButton
          size="small"
          component="a"
          href={value}
          target="_blank"
          rel="noreferrer"
          sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, ml: 0.5 }}
        >
          <ExternalLink size={14} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
